import {
  NAV_MODULE_DEFINITIONS,
  OPERATIONAL_FALLBACK_MODULES,
  type NavModuleKey,
} from '@/config/navigationModules';

export type OperationalModulesMap = Partial<Record<NavModuleKey, { operational: boolean }>>;

export function getPermissionVisibleModules(
  canAccessPath: (path: string) => boolean,
  isSuperAdmin: boolean
): Set<NavModuleKey> {
  if (isSuperAdmin) {
    return new Set(NAV_MODULE_DEFINITIONS.map((m) => m.key));
  }
  const visible = new Set<NavModuleKey>();
  for (const mod of NAV_MODULE_DEFINITIONS) {
    const gates = mod.moduleTabGateRoutes;
    if (gates?.length) {
      if (gates.some((route) => canAccessPath(route))) {
        visible.add(mod.key);
      }
      continue;
    }
    if (mod.routeKeys.some((route) => canAccessPath(route))) {
      visible.add(mod.key);
    }
  }
  return visible;
}

export function mergeModuleVisibility(
  permissionVisible: Set<NavModuleKey>,
  operationalMap: OperationalModulesMap | undefined,
  isSuperAdmin: boolean,
  operationalFailOpen: boolean
): Set<NavModuleKey> {
  if (isSuperAdmin) {
    return new Set(NAV_MODULE_DEFINITIONS.map((m) => m.key));
  }
  const result = new Set<NavModuleKey>();
  /** Until the operational map has loaded, keep permission-visible modules (fail-open). */
  const mapLoaded = operationalMap !== undefined;
  for (const key of permissionVisible) {
    if (OPERATIONAL_FALLBACK_MODULES.includes(key)) {
      result.add(key);
      continue;
    }
    const entry = operationalMap?.[key];
    const operational =
      entry?.operational ??
      (operationalFailOpen || !mapLoaded ? true : OPERATIONAL_FALLBACK_MODULES.includes(key));
    if (operational) {
      result.add(key);
    }
  }
  return result;
}

export function getFirstVisibleModuleLanding(visible: Set<NavModuleKey>): string {
  const order = NAV_MODULE_DEFINITIONS.map((m) => m.key);
  for (const key of order) {
    if (visible.has(key)) {
      const def = NAV_MODULE_DEFINITIONS.find((m) => m.key === key);
      return def?.landingRoute ?? '/home';
    }
  }
  return '/home';
}
